import { useEffect, useRef, useState } from "react";

import { useChartsContext } from "contexts/ChartContext";
import styled from "styled-components";
import { ChartActionType } from "types/echarts";

import { useChartEntities, useResizeObserver } from "hooks/charts";
import { setPointerOverChart } from "hooks/charts/echart/tooltipVisibility";
import { useRigFracActivityController } from "hooks/charts/useRigFracActivityController";
import { useRigFracChartOptions } from "hooks/charts/useRigFracChartOptions";

import { BusyIndicator } from "components/activity/shared";
import { ErrorBoundary } from "components/base";
import { IconSpinner } from "components/icons";

export const RIG_FRAC_ERROR_TEST_ID = "rig-frac-error-wrapper";
export const RIG_FRAC_NO_MATCH_IN_WINDOW_TEST_ID = "rig-frac-no-match-in-window";

/**
 * ECharts host for both Rig/Frac charts (EVA-76).
 *
 * A slim fork of Chart/EChartWrapper: the shared render path (the 18 wells-specific
 * EChartWrapper hooks — group-by/filter/series-style/type-well) is wells-bound, so
 * only the chart-type-agnostic pieces are reused (echarts lifecycle, resize
 * observer). Option-building + the screenshot transform live in useRigFracChartOptions
 * (the lean equivalent of the wells useChartOptions), which runs the SHARED
 * getChartScreenshotOptions in screenshot mode.
 */
function RigFracChart() {
  const { id } = useChartsContext();
  const domRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<Nullable<echarts.ECharts>>(null);
  // useChartInstance offers no ready signal, and the first response can land
  // before the async echarts import resolves — this flag re-runs the option
  // effect (useRigFracChartOptions) once the instance exists.
  const [isInstanceReady, setIsInstanceReady] = useState(false);
  const hoverLegendIdRef = useRef("");
  const seriesIdsRef = useRef<string[]>([]);

  const { screenshot, dataTable } = useChartEntities(id);
  const { loading, error, showNoMatchInWindow } = useRigFracActivityController();
  // Mirrors RigFracChartWrapper's `!isDataTableActive` suppression on its own
  // empty-state Indicator: the data table overlays this component rather than
  // replacing it, so without this guard the overlay paints over the raw data
  // grid (z-index collision — see RigFracChart.test.tsx).
  const isDataTableActive = dataTable?.active ?? false;

  const isScreenshotActive = screenshot?.active ?? false;
  // Size the host to the preset while capturing so the chart fills the frame
  // instead of overflowing it (bug: chart doesn't fit the screenshot).
  const preset = (screenshot?.properties?.preset ?? {}) as {
    width?: number;
    height?: number;
  };
  const screenshotWidth = isScreenshotActive ? preset.width : undefined;
  const screenshotHeight = isScreenshotActive ? preset.height : undefined;

  // echarts instance lifecycle (mirrors useChartInstance + readiness flag).
  useEffect(() => {
    let isMounted = true;
    const chartDom = domRef.current;
    if (!chartDom) return;
    import("hd-echarts/echarts")
      .then((echarts) => {
        if (!domRef.current || !isMounted) return;
        const instance = echarts.init(domRef.current);
        chartInstanceRef.current = instance;
        instance.on(ChartActionType.Hover, handleSeriesHover);
        instance.on(ChartActionType.LineBlur, handleSeriesBlur);
        setIsInstanceReady(true);
      })
      .catch((err) => {
        throw new Error(`Error initializing hd chart instance:\n ${err.message}`);
      });

    // Hide the tooltip on pointer-leave with tooltip.show:false (EVA-6690, the
    // same mechanism useChartMouseInteractions gives the wells charts — this
    // host doesn't use that wells-bound hook). HideTip/triggerOn alone are
    // defeated for an `enterable` tooltip: a re-render re-shows it via
    // ECharts' _keepShow, and the cursor resting on the tooltip sets
    // _inContent, which makes hideLater skip the hide — so the tooltip stayed
    // stuck on widget→widget moves. Removing the component is the only
    // reliable dismissal; the shared pointer-over flag keeps it off across
    // notMerge rebuilds (useRigFracChartOptions honours it).
    const setTooltipShown = (shown: boolean) => {
      const instance = chartInstanceRef.current;
      if (!instance || instance.isDisposed()) return;
      instance.setOption({ tooltip: { show: shown } });
    };
    // The hd-echarts fork's own hover/lineBlur events (standard mouseover never
    // fires for a line's polyline); both carry the element's seriesIndex only.
    const seriesIdAt = (seriesIndex?: number): string =>
      (seriesIndex != null && seriesIdsRef.current[seriesIndex]) || "";
    // The axis tooltip skips its formatter while the hovered column and series
    // set are unchanged, and hover/lineBlur don't trigger a tooltip update.
    // HideTip drops that cache so the next pointer update re-runs the formatter
    // with the new id.
    const setHoveredId = (id: string) => {
      if (hoverLegendIdRef.current === id) return;
      hoverLegendIdRef.current = id;
      const instance = chartInstanceRef.current;
      if (instance && !instance.isDisposed()) {
        instance.dispatchAction({ type: ChartActionType.HideTip });
      }
    };
    const handleSeriesHover = (e: { seriesIndex?: number }) => {
      setHoveredId(seriesIdAt(e.seriesIndex));
    };
    // Only clear for the series being left, so a blur that lands after the next
    // series' hover can't wipe the newer id.
    const handleSeriesBlur = (e: { seriesIndex?: number }) => {
      if (hoverLegendIdRef.current === seriesIdAt(e.seriesIndex)) setHoveredId("");
    };
    const handleDomMouseEnter = () => {
      setPointerOverChart(id, true);
      setTooltipShown(true);
    };
    const handleDomMouseLeave = () => {
      hoverLegendIdRef.current = "";
      setPointerOverChart(id, false);
      setTooltipShown(false);
      const instance = chartInstanceRef.current;
      if (instance && !instance.isDisposed()) {
        instance.dispatchAction({ type: "hideTip" });
      }
    };
    chartDom.addEventListener("mouseenter", handleDomMouseEnter);
    chartDom.addEventListener("mouseleave", handleDomMouseLeave);

    return () => {
      isMounted = false;
      chartDom.removeEventListener("mouseenter", handleDomMouseEnter);
      chartDom.removeEventListener("mouseleave", handleDomMouseLeave);
      const instance = chartInstanceRef.current;
      if (instance && !instance.isDisposed()) {
        instance.off(ChartActionType.Hover, handleSeriesHover);
        instance.off(ChartActionType.LineBlur, handleSeriesBlur);
        instance.dispose();
        chartInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domRef, id]);

  useResizeObserver(domRef, chartInstanceRef);

  // Translate the response + (in screenshot mode) apply the shared screenshot
  // transform, push options to the instance, and persist the live axis options —
  // the lean equivalent of the wells useChartOptions.
  useRigFracChartOptions(
    chartInstanceRef,
    isInstanceReady,
    hoverLegendIdRef,
    seriesIdsRef
  );

  // Resize the echarts canvas to match the host after a screenshot re-size (the
  // ResizeObserver is debounced; capture can fire before it settles).
  useEffect(() => {
    const instance = chartInstanceRef.current;
    if (instance && !instance.isDisposed()) instance.resize();
  }, [screenshotWidth, screenshotHeight, isInstanceReady]);

  return (
    <ErrorBoundary>
      <Wrapper
        onContextMenu={(e) => e.preventDefault()}
        className="ChartPlot"
        $screenshot={isScreenshotActive}
        $width={screenshotWidth}
        $height={screenshotHeight}>
        {/* Screenshot overlay portal target — same id convention as Chart.tsx */}
        <ScreenshotContainer id={`screenshot-container-${id}`} />
        <EChartsHost ref={domRef} id={id} data-testid={id} />
        {loading && (
          <BusyIndicator>
            <IconSpinner showBackground />
          </BusyIndicator>
        )}
        {error && (
          <ErrorMessageWrapper data-testid={RIG_FRAC_ERROR_TEST_ID}>
            Failed to load chart data
          </ErrorMessageWrapper>
        )}
        {/* Only fires when a filter is active, so a genuinely-empty
            unfiltered result never shows it. Copy is deliberately generic
            since an all-zero series can't be told apart client-side from
            "matches nothing anywhere" vs "matches exist outside this
            window". */}
        {!error && showNoMatchInWindow && !isDataTableActive && (
          <ErrorMessageWrapper data-testid={RIG_FRAC_NO_MATCH_IN_WINDOW_TEST_ID}>
            No activity matches the current filter
          </ErrorMessageWrapper>
        )}
      </Wrapper>
    </ErrorBoundary>
  );
}

export default RigFracChart;

const Wrapper = styled.div<{
  $screenshot?: boolean;
  $width?: number;
  $height?: number;
}>`
  flex-grow: 1;
  position: relative;
  background-color: ${(p) => (p.$screenshot ? "#fff" : "inherit")};

  /* During capture the host must be EXACTLY the preset size. The min/max:100%
     constraints (used to fill the widget live) would otherwise let the grid
     parent's definite full height override the explicit preset height, so the
     chart overflowed the screenshot frame — unset them while screenshotting.
     flex:none disables the always-on flex-grow:1 so the centered (flex) parent
     can't stretch the host past the preset WIDTH (bug: width didn't collapse). */
  ${(p) =>
    p.$screenshot && p.$width && p.$height
      ? `
        flex: none;
        width: ${p.$width}px;
        height: ${p.$height}px;
        min-width: unset;
        min-height: unset;
        max-width: unset;
        max-height: unset;
      `
      : `
        width: 100%;
        height: 100%;
        min-width: 100%;
        min-height: 100%;
        max-width: 100%;
        max-height: 100%;
      `}
`;

const EChartsHost = styled.div`
  width: 100%;
  height: 100%;

  /* Scrollable tooltip when a group-by yields many series — same as the wells
     stacked-bar chart (whose .chart-tooltip-container cap lives on its Wrapper).
     Paired with the tooltip's enterable:true so the pointer can enter and scroll. */
  .chart-tooltip-container {
    max-height: 250px;
    overflow-y: auto;
    /* Not redundant — see WellStatusChart for the full note. A shrink-to-fit
       tooltip plus overflow-y:auto (which forces overflow-x to compute to
       "auto") grows a stray horizontal scrollbar as soon as the vertical one
       appears and takes its width out of the content box. */
    overflow-x: hidden;
  }
`;

// Same portal target Chart.tsx provides for the Screenshot overlay; defined
// locally so the rig-frac module graph stays decoupled from the wells Chart.
const ScreenshotContainer = styled.div`
  position: absolute;
  display: grid;
  justify-content: center;
  pointer-events: none;
`;

const ErrorMessageWrapper = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(211, 211, 211, 0.424);
  z-index: 10;
  color: var(--color-text);
  font-weight: var(--fontWeightMedium);
`;
